import { ReactElement, useCallback, useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { FieldError } from 'react-hook-form';
import styled from 'styled-components';

import { ErrorMessage } from '../../../../../shared/Typhography';

const Wrapper = styled.div`
  position: relative;
`;

const DataRangeWrapper = styled.div`
  position: relative;
`;

const DataRangePopup = styled.div`
  position: absolute;
  right: 0;
  z-index: 1;
`;

const DateInput = styled.div`
  width: max-content;
  height: 50px;

  align-items: center;
  background-color: hsl(0, 0%, 100%);
  border-color: hsl(0, 0%, 80%);
  border-radius: 35px;
  border-style: solid;
  border-width: 1px;
  box-shadow: 0px 0px 3px #c4c4c4;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  position: relative;
  transition: all 100ms;
  box-sizing: border-box;
  border: 0;
  text-align: center;
  padding: 0 10px;
  color: hsl(0, 0%, 50%);
  cursor: default;

  &:hover {
    box-shadow: 0px 0px 15px #c4c4c4;
  }

  &.disabled {
    pointer-events: none;
    background-color: hsl(0, 0%, 95%);
  }
`;

const ClearDate = styled.div`
  border-left: solid 1px hsl(0, 0%, 80%);
  color: hsl(0, 0%, 50%);
  font-size: 20px;
  font-weight: 300;
  padding: 10px;
  margin-left: 5px;
  cursor: pointer;
`;

interface DaterangePickerProps {
  onChange?(date: [Date | null, Date | null], event?: React.SyntheticEvent<any, Event>): void;
  selected?: boolean;
  selectsRange?: true;
  startDate: Date | null;
  endDate: Date | null;
  minDate?: Date | null;
  error?: FieldError | any;
  inline?: boolean;
  isClearable?: boolean;
  isCollapsed?: boolean;
  label?: string;
  disabled?: boolean;
  alwaysAsArray?: boolean;
  dataInputComponent?: (
    dates: [Date | null, Date | null],
    collapsed: boolean,
    isCollapsed: (collapsed: boolean) => void,
  ) => ReactElement;
}

// TODO Extract this component into shared components
export default function DaterangePicker({
  onChange,
  selectsRange,
  inline,
  isClearable,
  startDate,
  endDate,
  minDate,
  label,
  disabled,
  error,
  alwaysAsArray,
  dataInputComponent,
}: DaterangePickerProps) {
  const [dates, setDates] = useState<[Date | null, Date | null]>([startDate, endDate]);
  const [collapsed, isCollapsed] = useState<boolean>(false);

  const { message: errorMessage } = error ?? {};

  const onChangeSelectedDate = useCallback(
    (date: [Date | null, Date | null]) => {
      if (alwaysAsArray && !Array.isArray(date)) {
        date = [date, null];
      }
      setDates(date);
      onChange && onChange(date);
    },
    [onChange, alwaysAsArray],
  );

  useEffect(() => {
    if (!onChange) return;

    if (isClearable && !dates[0] && !dates[1]) {
      onChange(dates);
      isCollapsed(false);
      return;
    }

    if (!selectsRange && dates[0]) {
      onChange(dates);
      isCollapsed(false);
      return;
    }

    if (selectsRange && dates[0] && dates[1]) {
      onChange(dates);
      isCollapsed(false);
      return;
    }
  }, [dates]);

  return (
    <Wrapper>
      <DataRangeWrapper>
        {dataInputComponent ? (
          dataInputComponent(dates, collapsed, isCollapsed)
        ) : (
          <DateInput
            className={`${disabled ? 'disabled' : ''} `}
            onClick={() => {
              if (disabled) return;
              isCollapsed(!collapsed);
            }}
          >
            {dates[0] && !dates[1]
              ? dates[0].toDateString()
              : dates[0] && dates[1]
              ? `${dates[0].toDateString()} - ${dates[1].toDateString()}`
              : label}

            {(dates[0] || dates[1]) && (
              <ClearDate
                onClick={(evt) => {
                  evt.stopPropagation();
                  setDates([null, null]);
                }}
              >
                x
              </ClearDate>
            )}
          </DateInput>
        )}

        {collapsed && (
          <DataRangePopup>
            <ReactDatePicker
              selected={dates[0]}
              onChange={onChangeSelectedDate}
              startDate={dates[0] ?? undefined}
              endDate={dates[1]}
              minDate={minDate}
              // We add this prop to avoid bug in lib https://github.com/Hacker0x01/react-datepicker/issues/2930
              disabledKeyboardNavigation
              selectsRange={selectsRange as any}
              inline={inline}
              isClearable={isClearable}
            />
          </DataRangePopup>
        )}
      </DataRangeWrapper>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Wrapper>
  );
}
